import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import {
  ngHelper,
  isNumber,
  IProductTypeSize,
  UiContextService,
  getStockQuantity,
  getDailyLimitQuantity,
  IProductTypeListMode,
  IProductTypeSizeProduct,
  getAvailableQuantityToAddToBuild,
} from '@cakeiteasy/web-utils';

import {
  getCartBuildsCount,
  getCartLinesByTypeId,
  isModalClickIgnored,
  sizeHasMultipleVariants
} from '../../helpers';

import { ICakeCartLine } from '../../../cart';

/** One variant's lines in the cart, summed over every build that contains it. */
interface IVariantCartTotals {
  count: number;
  price: number;
  /** builds (orderLineBuilderId) holding the variant; above 1 its row shows the cart actions instead of a counter */
  buildsCount: number;
  line: ICakeCartLine | null;
}

@Component({
  standalone: false,
  selector: 'pieces-group-type',
  templateUrl: './pieces-group-type.component.html',
  styleUrls: ['./pieces-group-type.component.scss']
})
export class PiecesGroupTypeComponent implements OnInit, OnChanges {
  @Input() productType: IProductTypeListMode;
  @Input() cartLines: ICakeCartLine[];
  @Input() showImage: boolean;

  @Output() onSaveToCart = new EventEmitter<{ cartLine: ICakeCartLine; productType: IProductTypeListMode }>();
  @Output() onRemoveCartLine = new EventEmitter<ICakeCartLine>();
  @Output() showModal = new EventEmitter<{
    productType: IProductTypeListMode;
    orderLineBuilderId?: string;
    isEdit?: boolean;
  }>();
  @Output() showCart = new EventEmitter<void>();

  public size: IProductTypeSize;
  public products: IProductTypeSizeProduct[];
  public currentLines: ICakeCartLine[];
  public variantTotalsById: Record<number, IVariantCartTotals> = {};
  public groupBuildsCount = 0;
  /** per variant: its counter's plus, limited by the quantity of the build the variant is in */
  public plusDisabledById: Record<number, boolean> = {};
  public stockLeft: number | null;
  public dailyLimit: number | null;

  public shouldShowModalOnAdd: boolean;

  constructor(public uiContext: UiContextService) {
  }

  ngOnInit() {
    this._init();
  }

  private _setLines(cartLines: ICakeCartLine[]) {
    this.currentLines = getCartLinesByTypeId(this.productType.id, cartLines);
    this.variantTotalsById = this.currentLines && this.buildVariantTotals(this.currentLines);
    this.groupBuildsCount = getCartBuildsCount(this.currentLines);

    this._setPlusDisabled();
  }

  ngOnChanges(changes: SimpleChanges) {
    ngHelper.ifChanged(changes, 'cartLines', (cartLines: ICakeCartLine[]) => {
      this._setLines(cartLines);
    });
  }

  public onAddToCartClick(productId: number) {
    // with several builds a direct add could not tell which build it belongs to: build a new one instead
    if (this.shouldShowModalOnAdd || this.groupBuildsCount > 1) {
      return this.onShowModal();
    }

    return this.saveToCart(productId);
  }

  public calcProductCountWrapper(variantId: number, productCount: number): (shiftCount: number) => number {
    return (shiftCount: number): number => {
      const maxToAdd = this._getAvailableToAdd(variantId);

      // we limit only increasing
      if (maxToAdd !== null && shiftCount > 0) {
        productCount += Math.min(maxToAdd, shiftCount);
      } else {
        productCount += shiftCount;
      }

      return Math.max(productCount, 0);
    };
  }

  public saveToCart(productId?: number) {
    const product = this.products.find(item => item.id === productId);
    const { variant } = product;
    const count = this._getInitialCount();

    const cartLine = {
      productId: product.id,
      count,
      bakeryId: this.productType.bakery.id,
      variant,
    } as ICakeCartLine;

    this.variantTotalsById[cartLine.variant.id] = { count, price: variant.price, buildsCount: 1, line: null };
    this._setPlusDisabled();
    this.onSaveToCart.emit({ cartLine, productType: this.productType });
  }

  public countUpdate(productId: number, count: number) {
    if (!this.currentLines) {
      return;
    }

    const cartLine = this.currentLines.find(cartLine => cartLine.productId === productId);
    this.variantTotalsById[cartLine.variant.id].count = count;
    this._setPlusDisabled();
    if (count === 0) {
      this.onRemoveCartLine.emit(cartLine);
    } else {
      this.onSaveToCart.emit({
        cartLine: {
          ...cartLine,
          count
        },
        productType: this.productType
      });
    }
  }

  public isVariantMultiBuild(variantId: number): boolean {
    return this.variantTotalsById[variantId]?.buildsCount > 1;
  }

  public onShowModal() {
    // with several builds there is no single one to add to or edit
    if (this.groupBuildsCount > 1) {
      return this.onAddNew();
    }

    const [line] = this.currentLines || [];
    this.showModal.emit({
      productType: this.productType,
      orderLineBuilderId: line?.orderLineBuilderId,
      isEdit: !!(this.currentLines && this.currentLines.length),
    });
  }

  /** Opens the build that holds this variant (it is in one build only). */
  public onEditVariant(variantId: number) {
    this.showModal.emit({
      productType: this.productType,
      orderLineBuilderId: this.variantTotalsById[variantId]?.line?.orderLineBuilderId,
      isEdit: true,
    });
  }

  public onAddNew() {
    this.showModal.emit({ productType: this.productType, isEdit: false });
  }

  public onShowCart() {
    this.showCart.emit();
  }

  public onProductTypeClick(event: MouseEvent) {
    if (isModalClickIgnored(event)) {
      return;
    }

    this.onShowModal();
  }

  private _getVariantsCount() {
    return Object.keys(this.variantTotalsById)
      .reduce((acc, key) => acc + this.variantTotalsById[key].count, 0);
  }

  /**
   * Quantity of the build a variant row adds to. One build (or none): the whole group, as before. Several builds: the
   * build holding the variant; a variant not in the cart goes to a new build, which starts empty.
   */
  private _getBuildQuantity(variantId?: number): number {
    if (this.groupBuildsCount <= 1) {
      return this._getVariantsCount();
    }

    const line = isNumber(variantId) ? this.variantTotalsById[variantId]?.line : null;
    if (!line) {
      return 0;
    }

    const builderId = line.orderLineBuilderId ?? null;
    return (this.currentLines || [])
      .filter((cartLine) => (cartLine.orderLineBuilderId ?? null) === builderId)
      .reduce((total, cartLine) => {
        const totals = this.variantTotalsById[cartLine.product.variant.id];
        // a variant in one build may have just changed on its counter, before the cart lines caught up
        return total + (totals?.buildsCount === 1 ? totals.count : cartLine.count);
      }, 0);
  }

  /** null = no limit. maxQuantity applies to the variant's build, the stock limit to the whole cart. */
  private _getAvailableToAdd(variantId?: number): number | null {
    return getAvailableQuantityToAddToBuild(this.productType, this._getBuildQuantity(variantId), this._getVariantsCount());
  }

  private _canAdd(quantityToAdd: number, variantId?: number): boolean {
    const available = this._getAvailableToAdd(variantId);
    return available === null || available >= quantityToAdd;
  }

  private _setPlusDisabled(): void {
    const step = this.productType.increment || 1;
    this.plusDisabledById = Object.keys(this.variantTotalsById || {}).reduce((acc, variantId) => {
      acc[variantId] = !this._canAdd(step, +variantId);
      return acc;
    }, {} as Record<number, boolean>);
  }

  public get isAddToCartDisabled(): boolean {
    return !this._canAdd(this._getInitialCount());
  }

  private _getInitialCount(): number {
    return this.productType.minQuantityPerProduct || this.productType.minQuantity || this.productType.increment || 1;
  }

  private buildVariantTotals(cartLines: ICakeCartLine[]): Record<number, IVariantCartTotals> {
    const linesByVariant = (cartLines || []).reduce((acc, line) => {
      const variantId = line.product.variant.id;
      (acc[variantId] = acc[variantId] || []).push(line);
      return acc;
    }, {} as Record<number, ICakeCartLine[]>);

    return Object.keys(linesByVariant).reduce((acc, variantId) => {
      const lines = linesByVariant[variantId];
      acc[variantId] = {
        count: lines.reduce((total, line) => total + line.count, 0),
        price: lines.reduce((total, line) => total + line.fullPrice, 0),
        buildsCount: getCartBuildsCount(lines),
        line: lines[0],
      };
      return acc;
    }, {} as Record<number, IVariantCartTotals>);
  }

  private _init() {
    const [size] = this.productType.sizes;
    this.size = size;
    this.products = size.products;
    this.shouldShowModalOnAdd = sizeHasMultipleVariants(size);
    this.stockLeft = getStockQuantity(this.productType);
    this.dailyLimit = getDailyLimitQuantity(this.productType);
  }
}
